import * as React from 'react';
import { Button, Row, Col, ButtonGroup, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, CardTitle, Spinner, InputGroupAddon, Collapse, Card, CardBody } from 'reactstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { AlertOnErrors } from '../../shared/alertOnErrors';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { Waypoint } from 'react-waypoint';
import { useReplaceSearchParamsEffect, useSearchParams } from '../../shared/useURLSearchParams';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../shared/searchInput/SearchInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MainContainer } from '../shared/MainContainer';
import { NoResultsFound } from '../shared/NoResultsFound';
import { StickyToolbar } from '../shared/StickyToolbar';
import { useHistory } from 'react-router';
import { ConditionalFragment } from 'react-conditionalfragment';
import { Banner } from '../shared/Banner';
import { useToggleStateArray, useToggleState } from 'use-toggle-state';
import { Background } from '../shared/background/Background';
import { AssessmentType } from '../../api/main/models/codeOnly/AssessmentType';
import { QuestionTagDisplay } from '../questionTags/questionTagSelector/QuestionTagDisplay';
import { useAssessmentsListViewModel } from '../../api/main/assessments/viewModels/useAssessmentListViewModel';
import { CardsOrTable } from '../shared/cardsOrTable/CardsOrTable';
import { usePreferredListViewMode } from '../../globalState/preferredListViewMode/usePreferredListViewMode';
import { FeedbackStyle, feedbackStyleDisplayName } from '../../api/main/models/codeOnly/FeedbackStyle';
import { useCloneAssessmentCallback } from '../../api/main/assessments/useCloneAssessmentCallback';
import { useAsyncCallback } from 'react-use-async-callback';
import { HtmlDisplay } from '../../shared/htmlEditor';
import { useCallback } from "react";
import { TagDisplay } from "../shared/tagFilter/TagDisplay";

export interface AssessmentsListBaseBaseProps {
    title: string,

    assessmentTypes: Array<AssessmentType>,
    baseRoute: string,
}

/**
 * Base list functionality for Assessments (including Learning).
 * 
 * You most often want to use the components that wrap this one rather than this one directly.
 */
export const AssessmentsListBase = (props: AssessmentsListBaseBaseProps) => {
    const {
        title,
        assessmentTypes,
        baseRoute,
    } = props;

    const { t } = useTranslation();
    const { search: searchParam } = useSearchParams();
    const [search, setSearch] = React.useState<string>(searchParam ?? '');
    const {
        data: { items: allItems, tags, links },
        isLoading, errors: loadingErrors, fetchMore, hasMore
    } = useAssessmentsListViewModel({ pageSize: undefined, assessmentTypes: assessmentTypes, });
    const [isMenuOpen, toggleMenuOpen] = useToggleStateArray();
    const history = useHistory();

    // Manage the toggling of tags on and off.
    const [selectedTags, setSelectedTags] = React.useState<Array<string>>([]);
    const toggleTagSelected = useCallback((tag: string) => {
        setSelectedTags(prevState => {
            const existing = prevState.find(it => it === tag);

            if (existing) {
                // If we are currently on, toggle to off.
                return prevState.filter(it => it !== tag);
            } else {
                // Add tag to the list of on toggles.
                return [...prevState, tag];
            }
        });
    }, [setSelectedTags]);
    const isTagSelected = React.useCallback((tag: string) => {
        // If no tags are selected, treat it as if all tags are selected.
        if (!selectedTags.length) {
            return true;
        }

        // Return true or false based on if this tag has been selected explictly.
        return selectedTags.find(it => it === tag);
    }, [selectedTags]);

    // Filter by the assessment's search client side so it can work when offline as well as online.
    const items = React.useMemo(() => {
        if (!allItems) {
            return allItems;
        }

        let ret = allItems;

        // Filter by selected tags if any tag filters have been selected.
        if (selectedTags.length) {
            ret = ret.filter(item => {
                // Get all questionFilterTags for this question.
                const myLinks = links.filter(link => link.targetId === item.id);

                for (const link of myLinks) {
                    if (isTagSelected(link.questionTagId)) {
                        return true;
                    }
                }

                return false;
            });
        }

        let lowerSearch = search.toLocaleLowerCase();

        if (lowerSearch) {
            // Filter the items being displayed.
            ret = ret.filter(item => {
                //Get the tags that are linked to this item.
                const myTags = tags?.filter(tag => !!links?.find(it => it.questionTagId === tag.id && it.targetId === item.id)) ?? [];




                // Filter the items being displayed.
                return (
                    item.name.toLocaleLowerCase().indexOf(lowerSearch) >= 0
                    || !!myTags.find(tag => tag.name.toLocaleLowerCase().indexOf(lowerSearch) >= 0)
                );
            });
        }

        return ret;
    }, [allItems, search, tags, links, isTagSelected, selectedTags.length]);

    useReplaceSearchParamsEffect({ search: search });

    const [viewMode, setViewMode] = usePreferredListViewMode();

    // Copy this assessment, and open it.
    const [cloneAssessment, { errors: cloneAssessmentErrors }] = useCloneAssessmentCallback();
    const [cloneAndEdit, { isExecuting: isCloning, errors: cloneAndEditErrors }] = useAsyncCallback(async (id: string) => {
        const cloned = await cloneAssessment(id);
        if (!cloned) {
            return;
        }

        history.push(`${baseRoute}/edit/${cloned.id}`);
    }, [cloneAssessment, baseRoute, history]);

    //controls for filtering by tags
    const [isTagFilterOpen, toggleTagFilterOpen] = useToggleState(false);

    return (
        <Background>
            <Banner fluid>
                <StickyToolbar>
                    <Row>
                        <Col xs={11} md="">
                            <h1>{title}</h1>
                        </Col>
                        <ConditionalFragment showIf={isLoading}>
                            <Col xs={1} md="auto">
                                <LoadingIndicator size="sm" />
                            </Col>
                        </ConditionalFragment>

                        <Col>
                            <SearchInput value={search} onChange={e => setSearch(e.currentTarget.value)}>
                            <InputGroupAddon addonType="append">
                                <Button className="search-input-button" onClick={() => toggleTagFilterOpen()}>
                                    <FontAwesomeIcon icon="tags" />
                                    <span className="sr-only">{t('assessmentsListBase.toggleTags', 'Toggle tags')}</span>
                                </Button>
                            </InputGroupAddon>
                            </SearchInput>
                        </Col>
                        <Col xs="auto">
                            <LinkContainer to={`${baseRoute}/add`}>
                                <Button color="primary">
                                    <FontAwesomeIcon icon="plus" /><> {t('assessmentsListBase.add', 'Add')}</>
                                </Button>
                            </LinkContainer>
                        </Col>
                        <Col xs={12} md="auto">
                            <ButtonGroup>
                                <Button color="secondary" outline={viewMode !== 'cards'} onClick={() => setViewMode('cards')}>
                                    <FontAwesomeIcon icon="th-large" />
                                    <span className="sr-only">{t('common.cards', 'Cards')}</span>
                                </Button>
                                <Button color="secondary" outline={viewMode !== 'table'} onClick={() => setViewMode('table')}>
                                    <FontAwesomeIcon icon="th-list" />
                                    <span className="sr-only">{t('common.list', 'List')}</span>
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>

                    <Collapse isOpen={isTagFilterOpen}>
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col xs={12} md="auto">
                                        <p className="text-muted">
                                            {t('questionsListBase.selectTagsToFilter', 'Select tags to filter by:')}
                                        </p>
                                    </Col>
                                    <Col>
                                        <TagDisplay noPadding>
                                            {
                                                tags?.map(item => (
                                                    <Button key={item.id} size="sm" color="secondary" outline={!isTagSelected(item.id)} onClick={() => toggleTagSelected(item.id)}>
                                                        {item.name}
                                                    </Button>
                                                ))
                                            }
                                        </TagDisplay>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Collapse>
                </StickyToolbar>
            </Banner>

            <MainContainer fluid>
                <AlertOnErrors errors={[loadingErrors, cloneAssessmentErrors, cloneAndEditErrors]} />
                <CardsOrTable
                    viewMode={viewMode}
                    items={items}
                    onItemClick={item => history.push(`${baseRoute}/edit/${item.id}`)}
                    tableHeadings={[
                        t('assessmentsListBase.name', 'Name'),
                        t('assessmentsListBase.tileText', 'Tile description'),
                        t('assesssmentsListBase.tags', 'Tags'),
                        t('assessmentsListBase.feedbackStyle', 'Feedback style'),
                        t('assessmentsListBase.date', 'Created date'),
                    ]}
                    columns={[
                        (item, view) => view === 'cards' ? (<CardTitle tag="h5">{item.name}</CardTitle>) : item.name,
                        (item) => <HtmlDisplay html={item.tileText} />,
                        (item) => (
                            <QuestionTagDisplay
                                tags={tags?.filter(tag => !!links?.find(it => it.questionTagId === tag.id && it.targetId === item.id)) ?? []}
                            />
                        ),
                        (item, view) => view === 'cards'? null: feedbackStyleDisplayName(item.feedbackStyle as FeedbackStyle, t),
                        (item, view) => view === 'cards' ? null : t('common.datetime', '{{date, DD/MM/YYYY HH:mm}}', { date: item.creationDate }),
                    ]}
                    buttons={(item) => (
                        <ButtonGroup>
                            <LinkContainer to={`${baseRoute}/edit/${item.id}`}>
                                <Button color="primary" outline>
                                    <FontAwesomeIcon icon="edit" />
                                    <> {t('common.edit', 'Edit')}</>
                                </Button>
                            </LinkContainer>
                            <ButtonDropdown isOpen={isMenuOpen(item.id)} toggle={() => toggleMenuOpen(item.id)}>
                                <DropdownToggle color="primary" outline caret>
                                    <span className="sr-only">{t('common.menuDropdown', 'More')}</span>
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem disabled={isCloning} onClick={() => cloneAndEdit(item.id)}>
                                        {
                                            isCloning ? (
                                                <>
                                                    <Spinner size="sm" />
                                                    <> </>
                                                    {t('assessmentsListBase.copying', 'Copying...')}
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon="clone" />
                                                    <> </>
                                                    {t('assessmentsListBase.createCopy', 'Create a copy')}
                                                </>
                                            )
                                        }
                                    </DropdownItem>
                                    <LinkContainer to={`${baseRoute}/delete/${item.id}`}>
                                        <DropdownItem className="text-danger">
                                            <FontAwesomeIcon icon="trash" />
                                            <> {t('common.delete', 'Delete')}</>
                                        </DropdownItem>
                                    </LinkContainer>
                                </DropdownMenu>
                            </ButtonDropdown>
                        </ButtonGroup>
                    )}
                />
                        <ConditionalFragment showIf={isLoading && !items?.length}>
                            <LoadingIndicator fullWidth />
                        </ConditionalFragment>
                        <ConditionalFragment showIf={!isLoading && !items?.length}>

                                <NoResultsFound search={search} />

                        </ConditionalFragment>
                        <ConditionalFragment showIf={!isLoading && hasMore()}>

                                <Waypoint key={items?.length ?? 0} onEnter={fetchMore} />
                                <LoadingIndicator fullWidth />

                        </ConditionalFragment>

            </MainContainer>
        </Background>
    );
};
